// frontend/src/utils/indicatorDisplayRegistry.ts

import type { Indicator } from '../state/types';

// ─────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────
export type FieldType = 'Number' | 'String';
export type EmittedFields = Record<string, FieldType>;

/** Where to draw the indicator: same chart as instrument, or its own chart below. */
export type ChartBehavior = 'overlay' | 'ownChart';

/** Which scale to use when overlay: price (OHLC) or volume (volume strip). */
export type IndicatorChartScale = 'price' | 'volume';

/** Passed to nameGenerator when the indicator's series is known (e.g. Rolling over another indicator). */
export type NameGeneratorOptions = {
  /** Default field for the series (instrument or dependent indicator). */
  defaultFieldForSeries?: string;
  /** Number of fields the series emits (1 = single-value, don't show field in label). */
  emittedFieldsCountForSeries?: number;
};

export type IndicatorDisplayInfo = {
  label: string;
  symbol: string;
  color: string;
  lineWidth?: number;
  nameGenerator?: (config: Record<string, any>, symbol: string, options?: NameGeneratorOptions) => string;

  // What this indicator outputs
  emits: EmittedFields;
  /** Preferred default field when opening series config for this indicator (e.g. 'Lower' for Bollinger) */
  defaultField?: string;

  /** Default chart behavior: overlay on instrument chart, or own chart below. */
  chartBehavior: ChartBehavior;
  /** When overlay: draw on price scale or volume strip scale. Use 'volume' when indicator's primary series is volume. */
  chartScale?: IndicatorChartScale;
  /** Optional per-field colors (key = emit field name). When set, chart uses colors[fieldKey] ?? color for each series. */
  colors?: Record<string, string>;
  /** Optional short legend label per emit key (Mac chart legend / future web). */
  fieldLegendLabels?: Record<string, string>;
};

// ─────────────────────────────────────────────────────────────────────
// Single unified registry
// ─────────────────────────────────────────────────────────────────────
export const indicatorDisplayRegistry: Record<string, IndicatorDisplayInfo> = {
  SMA: {
    label: 'Simple Moving Average',
    symbol: 'SMA',
    color: '#8b3a3a',
    lineWidth: 2,
    emits: { Value: 'Number' },
    fieldLegendLabels: { Value: 'SMA' },
    chartBehavior: 'overlay',
    chartScale: 'price',
    nameGenerator: (config: any, symbol: string) => {
      const period = config.period ?? 20;
      let name = `${symbol} SMA[${period}]`;
      const rawField = (config.field || 'Close').toString();
      if (rawField.toLowerCase() !== 'close') {
        const fieldLabel = rawField.charAt(0).toUpperCase() + rawField.slice(1);
        name += ` ${fieldLabel}`;
      }
      return name;
    },
  },

  EMA: {
    label: 'Exponential Moving Average',
    symbol: 'EMA',
    color: '#5a6c7d',
    lineWidth: 2,
    emits: { Value: 'Number' },
    fieldLegendLabels: { Value: 'EMA' },
    chartBehavior: 'overlay',
    chartScale: 'price',
    nameGenerator: (config: any, symbol: string) =>
      `${symbol} EMA[${config.period ?? 20}]`,
  },

  RSI: {
    label: 'Relative Strength Index',
    symbol: 'RSI',
    color: '#4a6572',
    lineWidth: 2,
    emits: { Value: 'Number' },
    fieldLegendLabels: { Value: 'RSI' },
    chartBehavior: 'ownChart',
    nameGenerator: (config: any, symbol: string) => {
      const period = config.period ?? config.lookback ?? 14;
      const lag = config.lag ?? 0;
      const lagPart = lag > 0 ? `,${lag}` : '';
      return `${symbol} RSI[${period}${lagPart}]`;
    },
  },

  rolling: {
    label: 'Rolling f(x)',
    symbol: 'ROLL',
    color: '#2d5a4a',
    lineWidth: 2,
    emits: { Value: 'Number' },
    fieldLegendLabels: { Value: 'Rolling' },
    chartBehavior: 'overlay',
    chartScale: 'price',
    nameGenerator: (config: any, symbol: string, options?: NameGeneratorOptions) => {
      const op = (config.operation || 'min').toLowerCase();
      const opLabel =
        op === 'stddev' ? 'StdDevS' : op === 'stddevp' ? 'StdDevP' : op === 'max' ? 'Max' : 'Min';
      const field = (config.field || 'close').toLowerCase();
      const period = config.period || 20;
      const lag = config.lag ?? 0;
      const lb = lag > 0 ? `,${lag}` : '';

      let name = `${symbol} Rolling ${opLabel}`;
      const defaultField = (options?.defaultFieldForSeries ?? 'close').toLowerCase();
      const count = options?.emittedFieldsCountForSeries ?? 2;
      const showField = count > 1 && field !== defaultField;
      if (showField) {
        const fieldLabel = field.charAt(0).toUpperCase() + field.slice(1);
        name += ` (${fieldLabel})`;
      }
      name += `[${period}${lb}]`;
      return name;
    },
  },

  bar_range: {
    label: 'Bar Range',
    symbol: 'BR',
    color: '#8a6b3a',
    lineWidth: 2,
    emits: { Value: 'Number' },
    fieldLegendLabels: { Value: 'Bar range' },
    chartBehavior: 'overlay',
    chartScale: 'price',
    nameGenerator: (config: any, symbol: string) => {
      const rangeType = config.rangeType || 'high-low';
      const optionLabel = rangeType === 'high-low' ? 'High - Low' : 'Open - Close';
      return `${symbol} Bar Range (${optionLabel})`;
    },
  },

  ROC: {
    label: 'Rate of Change',
    symbol: 'ROC',
    color: '#3d6b7a',
    lineWidth: 2,
    emits: { Value: 'Number' },
    fieldLegendLabels: { Value: 'ROC' },
    chartBehavior: 'ownChart',
    nameGenerator: (config: any, symbol: string) => {
      const lookback = config.lookback ?? 1;
      const lag = config.lag ?? 0;
      const lagPart = lag > 0 ? `,${lag}` : '';
      const initial = (config.initialField ?? 'close').toLowerCase();
      const final = (config.finalField ?? 'close').toLowerCase();
      const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
      if (initial === 'close' && final === 'close') {
        return `${symbol} ROC[${lookback}${lagPart}]`;
      }
      if (initial === final) {
        return `${symbol} ROC[${lookback}${lagPart}] ${cap(initial)}`;
      }
      return `${symbol} ROC[${lookback}${lagPart}] ${cap(initial)} to ${cap(final)}`;
    },
  },

  CarryForward: {
    label: 'Carry Forward',
    symbol: 'CF',
    color: '#6b5a7d',
    lineWidth: 2,
    emits: { Value: 'Number' },
    fieldLegendLabels: { Value: 'Carry forward' },
    chartBehavior: 'overlay',
    chartScale: 'price',
    nameGenerator: (config: any, symbol: string) => {
      const field = (config.field ?? 'close').toLowerCase();
      const from = (config.from ?? 'beginning_of_month').toString();
      const fromLabel = from
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c: string) => c.toUpperCase());
      const fieldLabel = field.charAt(0).toUpperCase() + field.slice(1);
      return `${symbol} Carry Forward ${fieldLabel} (${fromLabel})`;
    },
  },

  BollingerBands: {
    label: 'Bollinger Bands',
    symbol: 'BB',
    color: '#4a6a9e',
    lineWidth: 2,
    emits: { Lower: 'Number', SMA: 'Number', Upper: 'Number' },
    defaultField: 'Lower',
    fieldLegendLabels: { Lower: 'Lower', SMA: 'Middle', Upper: 'Upper' },
    chartBehavior: 'overlay',
    chartScale: 'price',
    nameGenerator: (config: any, symbol: string) => {
      const period = config.period ?? 20;
      const stdDev = config.stdDev ?? 2;
      const lag = config.lag ?? 0;
      const lagPart = lag > 0 ? `,${lag}` : '';
      const field = (config.field ?? 'close').toLowerCase();
      if (field === 'close') {
        return `${symbol} BB[${period},${stdDev}${lagPart}]`;
      }
      const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
      return `${symbol} BB[${period},${stdDev}${lagPart}] ${cap(field)}`;
    },
  },

  PercentB: {
    label: '%B',
    symbol: '%B',
    color: '#4a6a9e',
    lineWidth: 2,
    emits: { Value: 'Number' },
    fieldLegendLabels: { Value: '%B' },
    chartBehavior: 'ownChart',
    nameGenerator: (config: any, symbol: string) => {
      const period = config.period ?? 20;
      const stdDev = config.stdDev ?? 2;
      const lag = config.lag ?? 0;
      const lagPart = lag > 0 ? `,${lag}` : '';
      const field = (config.field ?? 'close').toLowerCase();
      if (field === 'close') {
        return `${symbol} %B[${period},${stdDev}${lagPart}]`;
      }
      const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
      return `${symbol} %B[${period},${stdDev}${lagPart}] ${cap(field)}`;
    },
  },

  ADX: {
    label: 'ADX (Wilder)',
    symbol: 'ADX',
    color: '#5a6c7d',
    lineWidth: 2,
    emits: { PlusDI: 'Number', MinusDI: 'Number', ADX: 'Number' },
    defaultField: 'ADX',
    chartBehavior: 'ownChart',
    colors: {
      PlusDI: '#2d5a4a',
      MinusDI: '#8b3a3a',
      ADX: '#5a6c7d',
    },
    fieldLegendLabels: { PlusDI: '+DI', MinusDI: '\u2212DI', ADX: 'ADX' },
    nameGenerator: (config: any, symbol: string) => {
      const period = config.period ?? 14;
      const lag = config.lag ?? 0;
      const lagPart = lag > 0 ? `,${lag}` : '';
      return `${symbol} ADX[${period}${lagPart}]`;
    },
  },
};

// ─────────────────────────────────────────────────────────────────────
// Instrument emitted fields (hard-coded for now)
// ─────────────────────────────────────────────────────────────────────
export const INSTRUMENT_EMITTED_FIELDS: EmittedFields = {
  Date: 'String',
  Open: 'Number',
  High: 'Number',
  Low: 'Number',
  Close: 'Number',
  Volume: 'Number',
};

// ─────────────────────────────────────────────────────────────────────
// Helpers (existing + new minimal ones)
// ─────────────────────────────────────────────────────────────────────
export function getIndicatorDisplayName(
  indicator: Indicator,
  instrumentSymbol: string,
  options?: NameGeneratorOptions
): string {
  const entry = indicatorDisplayRegistry[indicator.type];
  if (!entry) return `${instrumentSymbol} ${indicator.type}`;
  return entry.nameGenerator
    ? entry.nameGenerator(indicator.config, instrumentSymbol, options)
    : `${instrumentSymbol} ${entry.label}`;
}

export function getIndicatorDisplayInfo(type: string) {
  return indicatorDisplayRegistry[type];
}

// What fields does this source emit?
export function getEmittedFields(source: string | 'instrument'): EmittedFields {
  if (source === 'instrument') return INSTRUMENT_EMITTED_FIELDS;
  const info = indicatorDisplayRegistry[source];
  return info?.emits ?? { Value: 'Number' }; // safe fallback
}

// Preferred default field when opening series config for a multi-field indicator
export function getDefaultFieldForIndicator(type: string): string | undefined {
  return indicatorDisplayRegistry[type]?.defaultField;
}

// Chart behavior and scale for an indicator type (defaults: overlay on price)
export function getChartBehavior(type: string): ChartBehavior {
  const info = indicatorDisplayRegistry[type];
  return info?.chartBehavior ?? 'overlay';
}

export function getIndicatorChartScale(type: string): IndicatorChartScale {
  const info = indicatorDisplayRegistry[type];
  return info?.chartScale ?? 'price';
}